import { Box, Flex, Text } from '@chakra-ui/react';
import { ReviewFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/review-fields/review-form-fields';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import ReviewTableScore from '../ReviewTableScore';
import ForAuthorReview from './ForAuthorReview';
import ForEditorReview from './ForEditorReview';
import ComplianceReview from './ComplianceReview';
import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { recommendationBadges } from '../Review/Badges';
import { DisplayField } from 'src/components/shared/DisplayField';

const PastReviewRevisionOverviewContent = ({
  revision,
  submissionContextId
}: {
  revision: IGetRevisionHistory;
  submissionContextId?: string;
}) => {
  const { reviewerFormSchema, organizationData } = useReviewData();
  const { reviewerRevisionFeedback } = revision;
  const reviewFormFields = new ReviewFormFields(
    reviewerFormSchema.documentSchema
  );
  const configs: any = {
    recommendation: reviewFormFields.getFieldConfig(
      'Recommendation'
    ) as unknown,
    // submission scores
    originality: reviewFormFields.getFieldConfig('Originality'),
    significance: reviewFormFields.getFieldConfig('Significance'),
    rigour: reviewFormFields.getFieldConfig('Rigour'),
    clarity: reviewFormFields.getFieldConfig('Clarity'),
    impact: reviewFormFields.getFieldConfig('Impact'),
    feedbackForAuthors: reviewFormFields.getFieldConfig('FeedbackForAuthors'),
    reviewerAttachments: reviewFormFields.getFieldConfig('ReviewerAttachments'),
    reviewerAttachmentsEditor: reviewFormFields.getFieldConfig(
      'ReviewerAttachmentsEditor'
    ),
    missingReference: reviewFormFields.getFieldConfig('MissingReference'),
    conflictOfInterest: reviewFormFields.getFieldConfig('ConflictOfInterest'),
    canArticleBeShortened: reviewFormFields.getFieldConfig(
      'CanArticleBeShortened'
    ),
    meritFurtherCoverageUponPublication: reviewFormFields.getFieldConfig(
      'MeritFurtherCoverageUponPublication'
    ),
    areaOfExpertise: reviewFormFields.getFieldConfig('AreaOfExpertise'),
    betterSuitedForAnotherJournal: reviewFormFields.getFieldConfig(
      'BetterSuitedForAnotherJournal'
    ),
    feedbackForEditors: reviewFormFields.getFieldConfig('FeedbackForEditors'),
    articleWellWritten: reviewFormFields.getFieldConfig('ArticleWellWritten'),
    unaddressedReviewComments: reviewFormFields.getFieldConfig(
      'UnaddressedReviewComments'
    ),
    // Opt Ins
    receiveFeedback: reviewFormFields.getFieldConfig('ReceiveFeedback'),
    webOfScienceOptIn: reviewFormFields.getFieldConfig('WebOfScienceOptIn'),
    peerReviewTransparency: reviewFormFields.getFieldConfig(
      'PeerReviewTransparency'
    ),
    reviewerAnonymity: reviewFormFields.getFieldConfig('ReviewerAnonymity'),
    consentToReInvite: reviewFormFields.getFieldConfig('ConsentToReInvite'),
    reviewConfirmation: reviewFormFields.getFieldConfig('ReviewConfirmation'),
    reviewersEthicalPolicy: reviewFormFields.getFieldConfig(
      'ReviewersEthicalPolicy'
    ),
    usedAI: reviewFormFields.getFieldConfig('UsedAI')
  };

  return reviewerRevisionFeedback?.map((revision, index: number) => {
    const { reviewAssessment } = revision;

    return index === 0 && reviewAssessment ? (
      <Flex flexDirection="column" gap={6}>
        {reviewAssessment?.recommendation && (
          <Flex flexDirection="column" gap={3}>
            <Text textStyle="headingGroup">Recommendation</Text>
            <Text>
              Make a decisive recommendation regarding the submission's
              publication.
            </Text>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="textBody">
                {recommendationBadges[reviewAssessment.recommendation]}
              </Text>
            </Flex>
          </Flex>
        )}
        {reviewAssessment?.submissionScores && (
          <Box flexDirection="column">
            <Text textStyle="headingItem">Submission Scores</Text>
            <Box marginX={2} marginTop={4}>
              <ReviewTableScore
                submissionScores={reviewAssessment?.submissionScores}
                reviewFieldConfig={configs}
              />
            </Box>
          </Box>
        )}

        {/* For Author */}
        {(reviewAssessment?.feedbackForAuthors?.original ||
          reviewAssessment?.reviewerAttachments?.length ||
          reviewAssessment?.missingReference) && (
          <ForAuthorReview
            reviewAssessment={reviewAssessment}
            configs={configs}
            submissionContextId={submissionContextId}
          />
        )}

        {/* For Editor */}

        {(reviewAssessment?.feedbackForEditors ||
          ('areaOfExpertise' in reviewAssessment &&
            reviewAssessment?.areaOfExpertise) ||
          reviewAssessment?.betterSuitedForAnotherJournal) && (
          <ForEditorReview
            reviewAssessment={reviewAssessment}
            configs={configs}
            organizationData={organizationData}
            submissionContextId={submissionContextId}
          />
        )}

        {/* Opt Ins */}
        {(('webOfScienceOptIn' in reviewAssessment &&
          reviewAssessment?.webOfScienceOptIn) ||
          reviewAssessment?.peerReviewTransparency ||
          reviewAssessment?.reviewerAnonymity ||
          ('receiveFeedback' in reviewAssessment &&
            reviewAssessment?.receiveFeedback)) && (
          <Flex direction="column" gap={4}>
            <Text textStyle="headingGroup">Opt Ins</Text>
            {'webOfScienceOptIn' in reviewAssessment &&
              reviewAssessment?.webOfScienceOptIn && (
                <DisplayField
                  fieldName="webOfScienceOptIn"
                  value={reviewAssessment?.webOfScienceOptIn}
                  config={configs}
                />
              )}
            {reviewAssessment?.peerReviewTransparency && (
              <DisplayField
                fieldName="peerReviewTransparency"
                value={reviewAssessment?.peerReviewTransparency}
                config={configs}
              />
            )}
            {reviewAssessment?.reviewerAnonymity && (
              <DisplayField
                fieldName="reviewerAnonymity"
                value={reviewAssessment?.reviewerAnonymity}
                config={configs}
              />
            )}
            {'receiveFeedback' in reviewAssessment &&
              reviewAssessment?.receiveFeedback && (
                <DisplayField
                  fieldName="receiveFeedback"
                  value={reviewAssessment?.receiveFeedback}
                  config={configs}
                />
              )}
          </Flex>
        )}

        {reviewAssessment?.consentToReInvite && (
          <Flex direction="column" gap={4}>
            <Text textStyle="headingGroup">Consent</Text>
            {reviewAssessment?.consentToReInvite && (
              <DisplayField
                fieldName="consentToReInvite"
                value={reviewAssessment?.consentToReInvite}
                config={configs}
              />
            )}
          </Flex>
        )}

        {/* Compliance */}
        {((configs.reviewConfirmation &&
          reviewAssessment?.reviewConfirmation) ||
          (configs.reviewersEthicalPolicy &&
            reviewAssessment?.reviewersEthicalPolicy)) && (
          <Flex direction={'column'} gap={3}>
            <Text textStyle={'headingGroup'}>Compliance</Text>

            {reviewAssessment?.reviewConfirmation && (
              <ComplianceReview
                config={configs.reviewConfirmation}
                value={reviewAssessment?.reviewConfirmation}
              />
            )}
            {reviewAssessment?.reviewersEthicalPolicy && (
              <ComplianceReview
                config={configs.reviewersEthicalPolicy}
                value={reviewAssessment?.reviewersEthicalPolicy}
              />
            )}
          </Flex>
        )}
      </Flex>
    ) : (
      <></>
    );
  });
};

export default PastReviewRevisionOverviewContent;
